/**
 * "Proje Klasörü" section inside the task files tab.
 *
 * Lists the shared-drive folder of the task's project so documents appear
 * right where work happens. Clicking a file opens the standard preview
 * modal (password-protected files show the built-in unlock prompt).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderOpen, LockKeyhole, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { sharedFilesApi } from "../../lib/copApi";
import { iconFor, formatSize } from "../../pages/cop/sharedFilesHelpers";
import SharedFilePreviewModal from "../../pages/cop/SharedFilePreviewModal";

export default function ProjectFolderFiles({ projectId }) {
  const { tenant } = useAuthStore();
  const navigate = useNavigate();
  const hasFilesModule = (tenant?.enabled_features || []).some(
    (f) => f === "cop" || f === "expo",
  );
  const filesBasePath = (tenant?.enabled_features || []).includes("cop")
    ? "/app/cop/files"
    : "/app/expo/files";

  const [folderId, setFolderId] = useState(null);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!projectId || !hasFilesModule) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post(`/projects/${projectId}/folder`);
        if (!alive) return;
        setFolderId(data.folder_id);
        const list = await sharedFilesApi.listFiles(data.folder_id);
        if (alive) setFiles(list || []);
      } catch {
        /* klasör yoksa/başarısızsa bölüm sessizce gizlenir */
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId, hasFilesModule]);

  if (!projectId || !hasFilesModule || !folderId) return null;

  return (
    <div className="mt-6" data-testid="project-folder-files">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-500">
          <FolderOpen className="h-3.5 w-3.5" />
          Proje Klasörü
        </div>
        <button
          type="button"
          onClick={() => navigate(`${filesBasePath}?tab=shared&folder=${folderId}`)}
          data-testid="project-folder-open-btn"
          className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-800"
        >
          Klasörü aç
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>

      {files.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-200 px-3 py-5 text-center text-xs text-zinc-500">
          Proje klasöründe henüz dosya yok — dosyaları proje sayfasına
          sürükleyip bırakabilirsin.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200">
          {files.map((f) => {
            const Icon = iconFor(f.mime_type);
            return (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => setPreview(f)}
                  data-testid={`project-folder-file-${f.id}`}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-zinc-50"
                >
                  <Icon className="h-4 w-4 shrink-0 text-zinc-400" />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                    {f.original_name || f.name}
                  </span>
                  {f.has_password && (
                    <LockKeyhole className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                  )}
                  <span className="shrink-0 text-[11px] text-zinc-400">
                    {formatSize(f.size)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {preview && (
        <SharedFilePreviewModal file={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
